import { useRef, useState, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import { Text, RenderTexture, PerspectiveCamera } from '@react-three/drei'
import { motion, AnimatePresence } from 'framer-motion'
import * as THREE from 'three'
import useCameraStore from './store'

// Animated text component with typing effect
function AnimatedText({ text, fontSize, color }) {
  const [displayText, setDisplayText] = useState('')
  const timeoutRef = useRef(null)
  
  useEffect(() => {
    setDisplayText('')
    let currentIndex = 0
    let mounted = true
    
    const typeNextChar = () => {
      if (!mounted) return
      
      if (currentIndex <= text.length) {
        setDisplayText(text.slice(0, currentIndex))
        currentIndex++
        timeoutRef.current = setTimeout(typeNextChar, 150)
      } else {
        // Pause before restarting
        timeoutRef.current = setTimeout(() => {
          if (!mounted) return
          currentIndex = 0
          setDisplayText('')
          typeNextChar()
        }, 2000)
      }
    }
    
    typeNextChar()
    
    return () => {
      mounted = false
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [text])
  
  // Check if text starts with coffee icon
  const hasIcon = displayText.startsWith('\uEC15')
  const iconPart = hasIcon ? displayText.slice(0, 1) : ''
  const textPart = hasIcon ? displayText.slice(1) : displayText
  const cursor = displayText.length < text.length ? '_' : ''
  
  if (hasIcon && iconPart) {
    return (
      <group>
        <Text 
          font="/Gohu/GohuFont14NerdFontMono-Regular.ttf" 
          position={[-1.2, 0, 0]} 
          fontSize={fontSize * 2} 
          letterSpacing={0.05}
          color={color}
          anchorX="center"
          anchorY="middle"
        >
          {iconPart}
        </Text>
        <Text 
          font="/Gohu/GohuFont14NerdFontMono-Regular.ttf" 
          position={[0.3, 0, 0]} 
          fontSize={fontSize} 
          letterSpacing={0.05}
          color={color}
          anchorX="left"
          anchorY="middle"
        >
          {textPart + cursor}
        </Text>
      </group>
    )
  }
  
  return (
    <Text 
      font="/Gohu/GohuFont14NerdFontMono-Regular.ttf" 
      position={[0, 0, 0]} 
      fontSize={fontSize} 
      letterSpacing={0.05}
      color={color}
      anchorX="center"
      anchorY="middle"
    >
      {displayText + cursor}
    </Text>
  )
}

// Simple text screen without the computer model
export function TextScreen({ text = "COFFEE CODED", position, rotation, scale = 1, invert = false, animated = false, fontSize = 1.2, pages = null, screenId = Math.random(), onClick = null, isClickable = false }) {
  const textRef = useRef()
  const rand = Math.random() * 10000
  const [currentPage, setCurrentPage] = useState(0)
  const [hovered, setHovered] = useState(false)
  const [pressed, setPressed] = useState(false)
  
  // Handle page switching with smooth transitions and alpha blend
  useEffect(() => {
    if (pages && pages.length > 1) {
      const interval = setInterval(() => {
        // Start fade out with slide animation
        setFadeOpacity(0.3)  // Start with partial opacity for blend effect
        setSlideOffset(-0.15)

        setTimeout(() => {
          setFadeOpacity(0)  // Full fade out
          setTimeout(() => {
            setCurrentPage((prev) => (prev + 1) % pages.length)
            // Slide in from right with alpha blend
            setSlideOffset(0.15)
            setFadeOpacity(0.3)  // Start fading in
            setTimeout(() => {
              setFadeOpacity(1)  // Complete fade in
              setSlideOffset(0)  // Complete slide
            }, 100)
          }, 150)
        }, 150)
      }, 3000) // Switch every 3 seconds
      return () => clearInterval(interval)
    }
  }, [pages])
  
  const currentText = pages ? pages[currentPage].text : text
  const currentFontSize = pages ? (pages[currentPage].fontSize || 1.2) : fontSize
  const currentAnimated = pages ? (pages[currentPage].animated || false) : animated
  const [fadeOpacity, setFadeOpacity] = useState(1)
  const [slideOffset, setSlideOffset] = useState(0)
  
  useFrame((state, delta) => {
    if (textRef.current && !currentAnimated && !hovered) {
      const time = state.clock.elapsedTime

      // Arcade-style cut bouncing (Pong/Pacman style)
      const bounceSpeed = 1
      const bounceHeight = 4  // Increased from 0.08
      const bounceWidth = 7   // Increased from 0.3

      // Create sharp, linear movements (triangle wave pattern)
      const verticalPhase = ((time * bounceSpeed) + rand) % 2
      const horizontalPhase = ((time * bounceSpeed * 0.7) + rand * 2) % 2

      // Sharp vertical bounce (triangle wave)
      let yOffset = 0
      if (verticalPhase < 1) {
        yOffset = bounceHeight * verticalPhase
      } else {
        yOffset = bounceHeight * (2 - verticalPhase)
      }

      // Sharp horizontal movement (triangle wave)
      let xOffset = 0
      if (horizontalPhase < 1) {
        xOffset = bounceWidth * horizontalPhase
      } else {
        xOffset = bounceWidth * (2 - horizontalPhase)
      }

      // Apply cut position changes (no lerp for sharper movement)
      textRef.current.position.x = (xOffset - bounceWidth/2) + slideOffset
      textRef.current.position.y = yOffset - bounceHeight/2

      // Smooth rotation based on bounce position
      let targetRotation = 0

      // Smooth horizontal rotation based on position
      // Use sine-like smoothing near edges
      if (horizontalPhase < 0.5) {
        // Moving right - tilt gradually
        targetRotation = -0.2 * Math.pow(1 - horizontalPhase * 2, 2)
      } else if (horizontalPhase < 1.5) {
        // Moving left - tilt opposite
        targetRotation = 0.2 * Math.pow((horizontalPhase - 0.5) * 2 - 1, 2)
      } else {
        // Back to right - tilt back
        targetRotation = -0.2 * Math.pow((horizontalPhase - 1.5) * 2, 2)
      }

      // Add smooth vertical influence
      if (verticalPhase < 0.5) {
        // Moving down - add gradual tilt
        targetRotation += 0.1 * (0.5 - verticalPhase)
      } else if (verticalPhase < 1.5) {
        // Moving up - opposite tilt
        targetRotation -= 0.1 * (verticalPhase - 1)
      } else {
        // Back down
        targetRotation += 0.1 * (verticalPhase - 2)
      }

      // Smooth lerp to target rotation
      const currentRotation = textRef.current.rotation.z || 0
      textRef.current.rotation.z = THREE.MathUtils.lerp(currentRotation, targetRotation, 0.3)
    }
  })

  return (
    <group 
      position={position} 
      rotation={rotation} 
      scale={scale}
    >
      <mesh
        onClick={isClickable ? (e) => {
          if (!onClick) return
          // Add tactile feedback for mobile
          if ('vibrate' in navigator) {
            navigator.vibrate(10)
          }
          setPressed(true)
          setTimeout(() => setPressed(false), 150)
          onClick(e)
        } : undefined}
        onPointerDown={isClickable ? () => setPressed(true) : undefined}
        onPointerUp={isClickable ? () => setPressed(false) : undefined}
        onPointerCancel={isClickable ? () => setPressed(false) : undefined}
        onPointerOver={isClickable ? () => {
          setHovered(true)
          document.body.style.cursor = 'pointer'
        } : undefined}
        onPointerOut={isClickable ? () => {
          setHovered(false)
          setPressed(false)
          document.body.style.cursor = 'auto'
        } : undefined}
      >
        <planeGeometry args={[2, 1.33]} />
        <meshBasicMaterial toneMapped={false}>
          <RenderTexture width={512} height={384} attach="map" anisotropy={16}>
            <PerspectiveCamera makeDefault manual aspect={4/3} position={[0, 0, 10]} />
            <color attach="background" args={[
              pressed ? (invert ? 'black' : '#35c19f') : (invert ? (hovered ? '#35c19f' : 'black') : (hovered ? 'black' : '#35c19f'))
            ]} />
            <ambientLight intensity={0.5} />
            {currentAnimated ? (
              <AnimatedText
                text={currentText}
                fontSize={currentFontSize}
                color={
                  pressed ? (invert ? '#35c19f' : 'black') : (invert ? (hovered ? 'black' : '#35c19f') : (hovered ? '#35c19f' : 'black'))
                }
              />
            ) : (
              <Text
                font="/Gohu/GohuFont14NerdFontMono-Regular.ttf"
                position={[0, 0, 0]}
                ref={textRef}
                fontSize={pressed ? currentFontSize * 1.1 : (hovered && invert ? currentFontSize * 1.2 : currentFontSize)}
                letterSpacing={0.05}
                color={
                  pressed ? (invert ? '#35c19f' : 'black') : (invert ? (hovered ? 'black' : '#35c19f') : (hovered ? '#35c19f' : 'black'))
                }
                fontWeight={hovered && invert ? 900 : 400}
                outlineWidth={hovered && invert ? 0.05 : 0}
                outlineColor="black"
                anchorX="center"
                anchorY="middle"
                material-transparent={true}
                material-opacity={fadeOpacity}
              >
                {currentText}
              </Text>
            )}
          </RenderTexture>
        </meshBasicMaterial>
      </mesh>
      {/* Frame around screen */}
      {/* <mesh position={[0, 0, -0.01]}>
        <planeGeometry args={[2.1, 1.6]} />
        <meshStandardMaterial color="#2a2a2a" />
      </mesh> */}
    </group>
  )
}

// Multiple text screens positioned in space
export function TextScreens({ onProjectsClick }) {
  return (
    <group>
      <TextScreen
        screenId="screen1"
        pages={[
          { text: "\uEC15", fontSize: 8 },  // Large coffee icon from Nerd Font
          // { text: "COFFEE CODED", fontSize: 0.8 },
        ]}
        position={[0.56, 1.43, 1.04]}
        rotation={[-Math.PI / 8, Math.PI / 120, 0]}
        scale={0.28}
        invert
        isClickable={true}
        onClick={(e) => {
          if (e) e.stopPropagation()
          // Screen 1: Toggle zoom and trigger projects overlay
          useCameraStore.getState().handleElementClick('screen1', 0)
          // Always trigger the projects overlay regardless of zoom state
          onProjectsClick()
        }}
      />

      {/* <TextScreen 
        text="HELLO WORLD" 
        position={[0, 2.5, -2]} 
        rotation={[0, 0, 0]}
        invert
      /> */}
    </group>
  )
}